<script lang="ts">
    import Fa from 'svelte-fa'
    import { faArrowsLeftRight, faCamera, faRotate, faXmark } from '@fortawesome/free-solid-svg-icons'
    import FieldHeading from './field-heading.svelte'
    import { PHOTO_MAX_HEIGHT, PHOTO_MAX_WIDTH, PHOTO_QUALITY } from '../../utils/image-limits'

    type Slot = 'before' | 'after'

    interface PhotoState {
        src: string | null
        offsetX: number
        offsetY: number
        zoom: number
    }

    interface BeforeAfterValue {
        before: PhotoState
        after: PhotoState
    }

    export let label: string
    export let data: Record<string, any>
    export let value: BeforeAfterValue = {
        before: { src: null, offsetX: 0, offsetY: 0, zoom: 1 },
        after: { src: null, offsetX: 0, offsetY: 0, zoom: 1 },
    }
    export let required = false
    export let readOnly = false

    // Escolha de visualização é feita por quem está vendo o formulário (visualizar/preenchimento),
    // não é configurada no construtor — por isso vive só como estado local deste componente.
    let viewMode: 'side' | 'overlay' = 'side'
    let sliderPercent = 50

    let stageEl: HTMLDivElement
    let beforeInputEl: HTMLInputElement
    let afterInputEl: HTMLInputElement

    type Interaction = { kind: 'divider' } | { kind: 'pan'; slot: Slot }
    let interaction: Interaction | null = null
    let panOrigin = { x: 0, y: 0 }

    function clamp(n: number, min: number, max: number) {
        return Math.min(max, Math.max(min, n))
    }

    function maxOffsetFor(zoom: number) {
        return Math.max(0, (zoom - 1) * 50)
    }

    function onSelectFile(slot: Slot, event: Event) {
        if (readOnly) return
        const input = event.currentTarget as HTMLInputElement
        const file = input.files?.[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = async () => {
            // Ver utils/image-limits.ts: o que sobe é o que todo mundo decodifica depois.
            const src = await window.api.utils.images.resizeBase64(reader.result as string, PHOTO_MAX_WIDTH, PHOTO_MAX_HEIGHT, PHOTO_QUALITY)
            value = { ...value, [slot]: { src, offsetX: 0, offsetY: 0, zoom: 1 } }
        }
        reader.readAsDataURL(file)
        input.value = ''
    }
    function removePhoto(slot: Slot) {
        if (readOnly) return
        value = { ...value, [slot]: { src: null, offsetX: 0, offsetY: 0, zoom: 1 } }
    }

    function panBy(slot: Slot, dxPercent: number, dyPercent: number) {
        if (readOnly) return
        const state = value[slot]
        const maxOffset = maxOffsetFor(state.zoom)
        value = {
            ...value,
            [slot]: {
                ...state,
                offsetX: clamp(state.offsetX + dxPercent, -maxOffset, maxOffset),
                offsetY: clamp(state.offsetY + dyPercent, -maxOffset, maxOffset),
            },
        }
    }

    function zoomBy(slot: Slot, deltaZoom: number) {
        if (readOnly) return
        const state = value[slot]
        if (!state.src) return
        const zoom = clamp(state.zoom + deltaZoom, 1, 4)
        const maxOffset = maxOffsetFor(zoom)
        value = {
            ...value,
            [slot]: {
                ...state,
                zoom,
                offsetX: clamp(state.offsetX, -maxOffset, maxOffset),
                offsetY: clamp(state.offsetY, -maxOffset, maxOffset),
            },
        }
    }

    // Interação no modo "lado a lado": cada frame já sabe a qual foto pertence.
    function onFramePointerDown(slot: Slot, event: PointerEvent) {
        if (readOnly || !value[slot].src) return
        interaction = { kind: 'pan', slot }
        panOrigin = { x: event.clientX, y: event.clientY }
    }

    function onFramePointerMove(slot: Slot, event: PointerEvent, frameEl: HTMLElement) {
        if (!interaction || interaction.kind !== 'pan' || interaction.slot !== slot) return
        const rect = frameEl.getBoundingClientRect()
        const dxPercent = ((event.clientX - panOrigin.x) / rect.width) * 100
        const dyPercent = ((event.clientY - panOrigin.y) / rect.height) * 100
        panBy(slot, dxPercent, dyPercent)
        panOrigin = { x: event.clientX, y: event.clientY }
    }

    function onFramePointerUp() {
        interaction = null
    }

    function onFrameWheel(slot: Slot, event: WheelEvent) {
        if (readOnly || !value[slot].src) return
        event.preventDefault()
        zoomBy(slot, event.deltaY < 0 ? 0.15 : -0.15)
    }

    // Interação no modo "sobreposto": um único palco decide, pela posição do clique,
    // se o usuário está pegando o divisor ou arrastando a foto que está por baixo do cursor.
    function slotUnderPointer(xPercent: number): Slot {
        return xPercent < sliderPercent ? 'before' : 'after'
    }

    function updateSliderFromEvent(event: PointerEvent) {
        const rect = stageEl.getBoundingClientRect()
        const pct = ((event.clientX - rect.left) / rect.width) * 100
        sliderPercent = clamp(pct, 0, 100)
    }

    function onStagePointerDown(event: PointerEvent) {
        const rect = stageEl.getBoundingClientRect()
        const xPercent = ((event.clientX - rect.left) / rect.width) * 100
        const handleHitPercent = (18 / rect.width) * 100
        if (Math.abs(xPercent - sliderPercent) <= handleHitPercent) {
            interaction = { kind: 'divider' }
            updateSliderFromEvent(event)
            return
        }
        const slot = slotUnderPointer(xPercent)
        if (readOnly || !value[slot].src) return
        interaction = { kind: 'pan', slot }
        panOrigin = { x: event.clientX, y: event.clientY }
    }

    function onStagePointerMove(event: PointerEvent) {
        if (!interaction) return
        if (interaction.kind === 'divider') {
            updateSliderFromEvent(event)
            return
        }
        const rect = stageEl.getBoundingClientRect()
        const dxPercent = ((event.clientX - panOrigin.x) / rect.width) * 100
        const dyPercent = ((event.clientY - panOrigin.y) / rect.height) * 100
        panBy(interaction.slot, dxPercent, dyPercent)
        panOrigin = { x: event.clientX, y: event.clientY }
    }

    function onStagePointerUp() {
        interaction = null
    }

    function onStageWheel(event: WheelEvent) {
        const rect = stageEl.getBoundingClientRect()
        const xPercent = ((event.clientX - rect.left) / rect.width) * 100
        const slot = slotUnderPointer(xPercent)
        if (readOnly || !value[slot].src) return
        event.preventDefault()
        zoomBy(slot, event.deltaY < 0 ? 0.15 : -0.15)
    }

    function photoTransform(state: PhotoState) {
        return `transform: translate(${state.offsetX}%, ${state.offsetY}%) scale(${state.zoom})`
    }
</script>

<div class="before-after-field">
    <FieldHeading {label} {required} {readOnly} />

    <div class="upload-slots">
        <div class="upload-slot">
            <span class="slot-label">Antes</span>
            {#if value.before.src}
                <div class="slot-preview">
                    <img src={value.before.src} alt="Antes" />
                    {#if !readOnly}
                        <div class="slot-overlay">
                            <button type="button" on:click={() => beforeInputEl.click()} title="Trocar foto">
                                <Fa icon={faRotate} />
                            </button>
                            <button type="button" on:click={() => removePhoto('before')} title="Remover foto">
                                <Fa icon={faXmark} />
                            </button>
                        </div>
                    {/if}
                </div>
            {:else}
                <button type="button" class="slot-empty" disabled={readOnly} on:click={() => beforeInputEl.click()}>
                    <Fa icon={faCamera} />
                    <span>Adicionar foto</span>
                </button>
            {/if}
            <input bind:this={beforeInputEl} type="file" accept="image/*" hidden on:change={(e) => onSelectFile('before', e)} />
        </div>

        <div class="upload-slot">
            <span class="slot-label">Depois</span>
            {#if value.after.src}
                <div class="slot-preview">
                    <img src={value.after.src} alt="Depois" />
                    {#if !readOnly}
                        <div class="slot-overlay">
                            <button type="button" on:click={() => afterInputEl.click()} title="Trocar foto">
                                <Fa icon={faRotate} />
                            </button>
                            <button type="button" on:click={() => removePhoto('after')} title="Remover foto">
                                <Fa icon={faXmark} />
                            </button>
                        </div>
                    {/if}
                </div>
            {:else}
                <button type="button" class="slot-empty" disabled={readOnly} on:click={() => afterInputEl.click()}>
                    <Fa icon={faCamera} />
                    <span>Adicionar foto</span>
                </button>
            {/if}
            <input bind:this={afterInputEl} type="file" accept="image/*" hidden on:change={(e) => onSelectFile('after', e)} />
        </div>
    </div>

    {#if value.before.src && value.after.src}
        <div class="compare-section">
            <div class="mode-switch">
                <button type="button" class:active={viewMode === 'side'} on:click={() => (viewMode = 'side')}>Lado a lado</button>
                <button type="button" class:active={viewMode === 'overlay'} on:click={() => (viewMode = 'overlay')}> Sobreposto </button>
            </div>

            {#if !readOnly}
                <p class="compare-instructions">Arraste sobre a foto para reposicionar • Use a rodinha do mouse para zoom</p>
            {/if}

            {#if viewMode === 'side'}
                <div class="side-by-side">
                    <figure>
                        <div
                            class="photo-frame"
                            on:pointerdown={(e) => onFramePointerDown('before', e)}
                            on:pointermove={(e) => onFramePointerMove('before', e, e.currentTarget)}
                            on:pointerup={onFramePointerUp}
                            on:pointerleave={onFramePointerUp}
                            on:wheel={(e) => onFrameWheel('before', e)}
                        >
                            <img src={value.before.src} alt="Antes" class="compare-img" style={photoTransform(value.before)} />
                        </div>
                        <figcaption>Antes</figcaption>
                    </figure>
                    <figure>
                        <div
                            class="photo-frame"
                            on:pointerdown={(e) => onFramePointerDown('after', e)}
                            on:pointermove={(e) => onFramePointerMove('after', e, e.currentTarget)}
                            on:pointerup={onFramePointerUp}
                            on:pointerleave={onFramePointerUp}
                            on:wheel={(e) => onFrameWheel('after', e)}
                        >
                            <img src={value.after.src} alt="Depois" class="compare-img" style={photoTransform(value.after)} />
                        </div>
                        <figcaption>Depois</figcaption>
                    </figure>
                </div>
            {:else}
                <div
                    class="compare-stage"
                    bind:this={stageEl}
                    on:pointerdown={onStagePointerDown}
                    on:pointermove={onStagePointerMove}
                    on:pointerup={onStagePointerUp}
                    on:pointerleave={onStagePointerUp}
                    on:wheel={onStageWheel}
                >
                    <div class="photo-frame">
                        <img src={value.after.src} alt="Depois" class="compare-img" style={photoTransform(value.after)} />
                    </div>
                    <div class="photo-frame" style="clip-path: inset(0 {100 - sliderPercent}% 0 0)">
                        <img src={value.before.src} alt="Antes" class="compare-img" style={photoTransform(value.before)} />
                    </div>
                    <div class="compare-handle" style="left: {sliderPercent}%">
                        <span class="handle-line" />
                        <span class="handle-knob"><Fa icon={faArrowsLeftRight} size="xs" /></span>
                    </div>
                    <span class="compare-tag compare-tag-left">Antes</span>
                    <span class="compare-tag compare-tag-right">Depois</span>
                </div>
            {/if}
        </div>
    {:else}
        <p class="compare-hint">Adicione as duas fotos para visualizar a comparação.</p>
    {/if}
</div>

<style>
    .upload-slots {
        display: flex;
        gap: 16px;
        flex-wrap: wrap;
    }
    .upload-slot {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
    .slot-label {
        font-size: 0.78rem;
        font-weight: 500;
        color: #868e96;
    }
    .slot-empty {
        width: 140px;
        height: 100px;
        border: 1px dashed #ced4da;
        border-radius: 10px;
        background: #f8f9fa;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 6px;
        color: #868e96;
        font-size: 0.78rem;
        cursor: pointer;
    }
    .slot-empty:hover:not(:disabled) {
        border-color: var(--cs2-accent, #4263eb);
        color: var(--cs2-accent, #4263eb);
    }
    .slot-empty:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
    .slot-preview {
        position: relative;
        width: 140px;
        height: 100px;
        border-radius: 10px;
        overflow: hidden;
        background: #f1f3f5;
    }
    .slot-preview img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }
    .slot-overlay {
        position: absolute;
        inset: 0;
        background: rgba(17, 19, 24, 0.45);
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        opacity: 0;
        transition: opacity 0.15s ease;
    }
    .slot-preview:hover .slot-overlay {
        opacity: 1;
    }
    .slot-overlay button {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        border: none;
        background: #fff;
        color: #495057;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
    }
    .slot-overlay button:hover {
        color: #212529;
    }
    .compare-hint {
        margin: 12px 0 0;
        font-size: 0.85rem;
        color: #adb5bd;
    }
    .compare-section {
        margin-top: 16px;
    }
    .mode-switch {
        display: inline-flex;
        gap: 4px;
        background: #f1f3f5;
        padding: 4px;
        border-radius: 9px;
        margin-bottom: 10px;
    }
    .mode-switch button {
        border: none;
        background: transparent;
        color: #495057;
        padding: 6px 14px;
        border-radius: 7px;
        font-size: 0.82rem;
        cursor: pointer;
    }
    .mode-switch button.active {
        background: #fff;
        color: var(--cs2-accent, #4263eb);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
    }
    .compare-instructions {
        margin: 0 0 10px;
        font-size: 0.74rem;
        color: #adb5bd;
    }
    .side-by-side {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
    }
    .side-by-side figure {
        flex: 1;
        min-width: 200px;
        margin: 0;
    }
    .side-by-side figcaption {
        text-align: center;
        font-size: 0.78rem;
        color: #868e96;
        margin-top: 6px;
    }
    .side-by-side .photo-frame {
        aspect-ratio: 4 / 3;
    }
    .photo-frame {
        position: relative;
        width: 100%;
        height: 100%;
        border-radius: 10px;
        overflow: hidden;
        background: #f1f3f5;
        touch-action: none;
        cursor: grab;
    }
    .compare-img {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        user-select: none;
        pointer-events: none;
    }
    .compare-stage {
        position: relative;
        width: 100%;
        max-width: 560px;
        aspect-ratio: 4 / 3;
        border-radius: 10px;
        overflow: hidden;
        background: #f1f3f5;
        touch-action: none;
        cursor: grab;
    }
    .compare-stage .photo-frame {
        position: absolute;
        inset: 0;
        border-radius: 0;
        cursor: default;
    }
    .compare-handle {
        position: absolute;
        top: 0;
        bottom: 0;
        transform: translateX(-50%);
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
    }
    .handle-line {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 50%;
        width: 3px;
        background: #fff;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15);
        transform: translateX(-50%);
    }
    .handle-knob {
        position: relative;
        width: 30px;
        height: 30px;
        border-radius: 50%;
        background: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
        color: #495057;
    }
    .compare-tag {
        position: absolute;
        bottom: 10px;
        font-size: 0.7rem;
        font-weight: 600;
        color: #fff;
        background: rgba(17, 19, 24, 0.55);
        padding: 3px 10px;
        border-radius: 999px;
        pointer-events: none;
    }
    .compare-tag-left {
        left: 10px;
    }
    .compare-tag-right {
        right: 10px;
    }
</style>
